import * as React from 'react';
import { Outside } from '@cian/ui-kit';

import { TFIOSuggestion, EGender } from '../../types/fio';
import { getFillProgress, placeholders, useOnChangeHandler, useOnClickItemHandler, useOnKeyDownHandler } from './utils';
import { useOnFocusHandler } from './utils/useOnFocusHandler';
import { useInputValueSubscribe } from './utils/useInputValueSubscribe';
import { useGetFIOSuggest } from './utils/useGetFIOSuggest';
import { FIOSuggest, Placeholder, Suggest } from '../../packages/FIOSuggest';

export interface IFIOSuggestContainerProps {
  onChange(inputValue: string, fillProgress: ReturnType<typeof getFillProgress>): void;
  error?: string;
  initialValue?: string;
}

export function FIOSuggestContainer({ onChange, initialValue, error }: IFIOSuggestContainerProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const [inputValue, setInputValue] = React.useState(initialValue || '');
  const [suggestPart, setSuggestPart] = React.useState('');
  /* istanbul ignore next */
  const [placeholder, setPlaceholder] = React.useState(initialValue ? '' : placeholders.default);
  const [gender, setGender] = React.useState(EGender.Unknown);
  const [suggestFiltered, setSuggestFiltered] = React.useState<TFIOSuggestion[]>([]);

  const getFIOSuggest = useGetFIOSuggest();

  const resetSuggested = React.useCallback(() => {
    setSuggestPart('');
    setSuggestFiltered([]);
  }, []);

  const handleOnFocus = useOnFocusHandler({ gender, inputValue, setSuggestFiltered, setSuggestPart, getFIOSuggest });

  const handleOnChange = useOnChangeHandler({
    suggestFiltered,
    setInputValue,
    setSuggestFiltered,
    gender,
    setGender,
    setSuggestPart,
    setPlaceholder,
    resetSuggested,
    getFIOSuggest,
  });

  const handleOnClickItem = useOnClickItemHandler({
    inputValue,
    suggestFiltered,
    inputRef,
    resetSuggested,
    setInputValue,
    setPlaceholder,
    setGender,
  });

  const handlePressTab = useOnKeyDownHandler({
    suggestPart,
    inputValue,
    handleOnChange,
  });

  useInputValueSubscribe({ onChange, inputValue });

  return (
    <Outside onOutside={resetSuggested}>
      <FIOSuggest
        error={error}
        inputRef={inputRef}
        onFocus={handleOnFocus}
        onChange={handleOnChange}
        inputValue={inputValue}
        onKeyDown={handlePressTab}
        placeholder={
          (suggestPart || placeholder) && (
            <Placeholder visiblePartText={suggestPart || placeholder} hidePartText={inputValue} />
          )
        }
        suggestFiltered={
          Boolean(suggestFiltered.length) && <Suggest suggest={suggestFiltered} handleClickItem={handleOnClickItem} />
        }
      />
    </Outside>
  );
}
